import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import { useTranslation } from "react-i18next";
import set from "@rill/set";
import getDayRangeString from "../../../utils/day-range-string";
import Chart from "../../../parts/chart";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import MetricsBackButton from "../../athlete-portal/metrics/metrics-back-button";
import { getChartLabels } from "../../../utils/chart-labels";
import NavigationComponent from "../../athlete-portal/calendar/components/NavigationComponent";
import { ReportsBackHandler } from "../../athlete-portal/calendar/components/ReportsBackHandler";
import SvgIcon from "../../../parts/SvgIcon";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { isPushOrPerformClient } from "../../../utils/user-helpers";

const {
  PERIOD_FORMATS,
  DATE_FORMATS: { DATE_PICKER_FORMAT },
} = require("../../../constants");

function View(props) {
  const { locals } = useRillContext();
  const { t } = useTranslation("dashboard");

  const {
    exercisesVolume,
    exercisesVolumeMean,
    query,
    search,
    feBranding = {},
  } = locals;
  const { period, units, mobileWV } = query;

  const period_short = PERIOD_FORMATS[period];
  const selectedMetrics =
    query.type === "total"
      ? exercisesVolume
      : _.pick(exercisesVolume, [query.type]);
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = getChartLabels(keys, query, period);

  const baselineVolume = locals.fromMetric(
    _.get(exercisesVolumeMean, query.type),
    units
  );
  const selectedMetricsGroups = _.values(selectedMetrics);
  const totalVolumes = _.map(keys, (key) =>
    locals.fromMetric(_.sumBy(selectedMetricsGroups, `${key}.volume`), units)
  );
  const volumeSeries = [
    {
      name: query.type,
      className: query.type,
      data: _.map(totalVolumes, (val) => val - baselineVolume),
    },
  ];

  return (
    <main
      role="reports-wrapper"
      className="Main Metrics"
      data-theme={mobileWV ? feBranding.appTheme : undefined}
      style={
        mobileWV
          ? {
              backgroundColor: "var(--background-color)",
              maxHeight: "unset",
              height: "100vh",
            }
          : null
      }
    >
      <NavigationComponent
        query={query}
        pathName={"/dashboard/metrics"}
        text={t("dashboard:metrics.calendarDayView")}
        classNames="mobile-hidden"
      />
      <article role="report-content" id="strength-metrics">
        <ReportsBackHandler
          title={t("dashboard:metrics.strengthMetrics")}
          locals={locals}
          customBackUrl={`/dashboard/metrics/${search}${
            mobileWV ? "&&randomReload=true" : ""
          }`}
        />

        <header>
          <hgroup className="metrics-title">
            <MetricsBackButton
              url={`/dashboard/metrics/${search}`}
              classNames="mobile-hidden"
            />
            <SvgIcon
              src="/img/new-icons/over-under-baseline-vol-ico.svg"
              className="metrics-icon mobile-hidden"
            />
            <div>
              <SvgIcon
                src="/img/new-icons/over-under-baseline-vol-ico.svg"
                className="metrics-icon only-show-mobile"
              />
              <h3>{t("dashboard:overUnderBaseline.title")}</h3>
              <ToggleLinksInput
                name="units"
                options={[
                  { text: t("dashboard:overUnderBaseline.kg"), value: "metric" },
                  { text: t("dashboard:overUnderBaseline.lbs"), value: "imperial" },
                ]}
                className={"only-show-mobile"}
              />
              <p className="p4 mobile-hidden">
                {t("dashboard:overUnderBaseline.description")}
              </p>
            </div>
            <p className="p4 only-show-mobile">
              {t("dashboard:overUnderBaseline.description")}
            </p>
          </hgroup>
          <hgroup className="metrics-dates">
            <div role="calendar-date-selectors">
              <DateInput
                name="start"
                placeholder={t("dashboard:metrics.startDate")}
                value={query.start}
                max={query.end}
                onChange={(date) => handleDayChange(date, "start")}
              />
              <span>{t("dashboard:metrics.to")}</span>
              <DateInput
                name="end"
                placeholder={t("dashboard:metrics.endDate")}
                value={query.end}
                min={query.start}
                onChange={(date) => handleDayChange(date, "end")}
                alignRight={true}
              />
            </div>
            <div>
              <ToggleLinksInput
                name="period"
                options={[
                  { text: t("dashboard:metrics.month"), value: "month" },
                  { text: t("dashboard:metrics.week"), value: "week" },
                  { text: t("dashboard:metrics.day"), value: "day" },
                ]}
                activePrimary={true}
              />
            </div>
          </hgroup>
          <hgroup className="metrics-data-options">
            <ToggleLinksInput
              name="units"
              options={[
                { text: t("dashboard:overUnderBaseline.kg"), value: "metric" },
                { text: t("dashboard:overUnderBaseline.lbs"), value: "imperial" },
              ]}
              className={"mobile-hidden"}
            />
            <ToggleLinksInput
              name="baselineTimeSpan"
              options={[
                { text: t("dashboard:overUnderBaseline.sixMonths"), value: "6-months" },
                { text: t("dashboard:overUnderBaseline.threeMonths"), value: "3-months" },
                { text: t("dashboard:overUnderBaseline.oneMonth"), value: "1-month" },
              ]}
              className={"mobile-hidden"}
            />
            <ToggleLinksInput
              className="tabs"
              name="type"
              options={[
                { text: t("dashboard:overUnderBaseline.total"), value: "total" },
                { text: t("dashboard:overUnderBaseline.squat"), value: "squat" },
                { text: t("dashboard:overUnderBaseline.bench"), value: "bench" },
                { text: t("dashboard:overUnderBaseline.deadlift"), value: "deadlift" },
              ]}
            />
          </hgroup>
        </header>
        <div className="Chart-Frame bar-chart">
          <div className="body">
            {!_.isEmpty(selectedMetrics) && (
              <div>
                <div className="legend">
                  <div className={`bar series ${query.type}`}>
                    {t("dashboard:overUnderBaseline.varianceFromBaseline")}
                  </div>
                </div>
                <div className="axis-labels">
                  <div className="left">{t("dashboard:overUnderBaseline.volume")}</div>
                  <div className="right">{t("dashboard:overUnderBaseline.variance")}</div>
                </div>
                <div className="chart-fullsize">
                  <div className="labels">
                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: volumeSeries }}
                      options={{
                        width: "60px",
                        height: "380px",
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          showGrid: false,
                          showLabel: true,
                          scaleMinSpace: 30,
                          onlyInteger: true,
                          referenceValue: 0,
                          labelInterpolationFnc: (val) => {
                            if (val === 0) return "-";
                            return val;
                          },
                        },
                      }}
                    />

                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: volumeSeries }}
                      options={{
                        width: "60px",
                        height: "380px",
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          showGrid: false,
                          showLabel: true,
                          referenceValue: 0,
                          scaleMinSpace: 30,
                          labelInterpolationFnc: (val) => {
                            if (val === 0) return `-     `;
                            return `${Math.round(
                              (val / baselineVolume) * 100
                            )}%`;
                          },
                        },
                      }}
                    />
                  </div>

                  <div className="data">
                    <Chart
                      type="Bar"
                      data={{
                        labels: labels,
                        series: volumeSeries,
                      }}
                      options={{
                        width: `${_.size(keys) * 40}px`,
                        height: "380px",
                        stackBars: true,
                        chartPadding: { left: 35, right: 75 },
                        axisX: { showGrid: false, showLabel: true },
                        axisY: {
                          showGrid: true,
                          showLabel: false,
                          scaleMinSpace: 30,
                        },
                      }}
                      tooltip={(item, i) => {
                        const key = keys[i];
                        const date = moment
                          .utc(query.start, DATE_PICKER_FORMAT)
                          .add(key, period);
                        const dayRange = getDayRangeString(
                          [
                            date,
                            date.clone().add(1, period).subtract(1, "day"),
                          ],
                          query
                        );
                        const totalVolume = totalVolumes[i];
                        return (
                          <div className="tooltip" style={{ left: item.x }}>
                            <div className="details">
                              <div>
                                <b>{dayRange}</b>
                              </div>
                              <div>
                                {t("dashboard:overUnderBaseline.volume")}{" "}
                                <b>{locals.withWeight(totalVolume, units)}</b>{" "}
                              </div>
                              <div>
                                {t("dashboard:overUnderBaseline.variance")}{" "}
                                <b>
                                  {_.round(
                                    ((totalVolume - baselineVolume) /
                                      baselineVolume) *
                                      100
                                  )}
                                  %
                                </b>
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </main>
  );
}

function handleDayChange(date, field) {
  console.log(date, field);
  submit("/actions/refresh", {
    method: "GET",
    body: { [field]: date },
  });
}

const OverUnderBaselineController = (app) => {
  app.get(
    "/over-under-baseline",
    set("heading", "Over/Under Baseline Volume"),
    async ({ req, res, load, locals, api, fail }) => {
      const { query } = req;

      _.defaults(req.query, {
        type: "total",
        period: "day",
        units: locals.units,
        baselineTimeSpan: "6-months",
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await Promise.all([
        load("exercisesVolume", {
          start: moment
            .utc(query.start, DATE_PICKER_FORMAT)
            .startOf("day")
            .toDate(),
          end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
          period: query.period,
        }),
        load("exercisesVolumeMean", {
          period: query.period,
          timeSpan: query.baselineTimeSpan,
          day: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
        }),
      ]);

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  type: "over-under-baseline",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["exercisesVolume", "exercisesVolumeMean"])
                      .assign({
                        period: query.period,
                        start: query.start,
                        end: query.end,
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);
          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }
      const { activeUser: user } = locals;
      if (isPushOrPerformClient(user) || user.coach) {
        await load("feBranding", {});
      }
      locals.modal = {
        share: <ShareModal />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default OverUnderBaselineController;
